import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormOf } from '@devinco/ui/legacy';
import { TranslateService } from '@ngx-translate/core';
import DateUtils from '@shared/utils/date.utils';

import {
  FullWeekdaysTranslationKeys,
  ReportFrequency,
  toggleableRecurrenceFormControls,
  WeekdaysFormKeys,
} from '@pages/reports-page/components/report-tabs/report-information-tab/constants';
import { ReportInformationTabForm } from '@pages/reports-page/components/report-tabs/report-information-tab/interfaces';
import { ReportInformationTabService } from './report-information-tab.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Injectable()
export class RecurrenceInfoService {
  private reportInfoTabService = inject(ReportInformationTabService);
  private translate = inject(TranslateService);

  setRecurrenceText(controls: FormOf<ReportInformationTabForm>): string {
    const recurrenceStartTime = controls.RecurrenceStartTime?.value
      ? DateUtils.convertDateTimeToNO(
          new Date(controls.RecurrenceStartTime.value),
          true
        )
      : '-';
    switch (controls.Frequency?.value) {
      case ReportFrequency.Daily:
        return this.setDailyRecurrenceText(controls, recurrenceStartTime);
      case ReportFrequency.Weekly: {
        return this.setWeeklyRecurrenceText(controls, recurrenceStartTime);
      }
      case ReportFrequency.Monthly: {
        return this.setMonthlyRecurrenceText(controls, recurrenceStartTime);
      }
      case ReportFrequency.Yearly: {
        return this.setYearlyRecurrenceText(controls, recurrenceStartTime);
      }
      default: {
        return '';
      }
    }
  }

  getRecurrenceArraysValues(
    frequency: ReportFrequency | undefined,
    byMonth: number | undefined,
    byDay: number[] | undefined,
    byMonthDay: number | undefined,
    offset: number | undefined
  ): Partial<ReportInformationTabForm> {
    const byDayVal = frequency === ReportFrequency.Weekly ? byDay : byDay?.[0];
    return {
      ByDay: byDayVal,
      ByMonthWithDay:
        frequency === ReportFrequency.Yearly &&
        !offset &&
        (byMonthDay || byMonth)
          ? byMonth
          : undefined,
      ByMonthWithOffset:
        frequency === ReportFrequency.Yearly && offset && (byDay || byMonth)
          ? byMonth
          : undefined,
      ByMonthDay: byMonthDay,
    };
  }

  setWeeklyRecurrenceControlValues(
    controls: FormOf<ReportInformationTabForm>
  ): void {
    // set weekday checkbox value depending on ByDay array values (numbers 0-6)
    Object.values(WeekdaysFormKeys).forEach(day => {
      controls[day].setValue(
        ((controls.ByDay?.value as number[] | undefined) ?? []).includes(
          Number(day)
        )
          ? true
          : false,
        { emitEvent: false }
      );
    });
  }

  setMonthlyRecurrenceControlValues(
    controls: FormOf<ReportInformationTabForm>
  ): void {
    controls.Interval.setValidators([Validators.min(1), Validators.max(31)]);
    if (controls.ByMonthDay?.value) {
      controls.onDay?.setValue(true);
      controls.onThe?.reset(false, { emitEvent: false });
      controls.on?.reset(false, { emitEvent: false });
    } else if (controls.Offset?.value || controls.ByDay?.value) {
      controls.onThe?.setValue(true);
      controls.onDay?.reset(false, { emitEvent: false });
      controls.on?.reset(false, { emitEvent: false });
    } else {
      controls.onDay?.setValue(true);
    }
  }

  setYearlyRecurrenceControlValues(
    controls: FormOf<ReportInformationTabForm>
  ): void {
    if (controls.ByMonthDay?.value || controls.ByMonthWithDay?.value) {
      controls.on?.setValue(true);
      controls.onDay?.reset(false, { emitEvent: false });
    } else if (
      controls.Offset?.value ||
      controls.ByDay?.value ||
      controls.ByMonthWithOffset?.value
    ) {
      controls.onThe?.setValue(true);
      controls.onDay?.reset(false, { emitEvent: false });
      controls.on?.reset(false, { emitEvent: false });
      controls.ByMonthWithDay?.disable();
      controls.ByMonthDay?.disable();
    } else {
      controls.on?.setValue(true);
    }
  }

  getSelectedWeekdays(formData: ReportInformationTabForm): number[] {
    const selectedValues: number[] = [];
    Object.values(WeekdaysFormKeys).forEach(weekday => {
      if (formData[weekday]) {
        selectedValues.push(Number(weekday));
      }
    });

    return selectedValues;
  }

  toggleFormElements(
    elements: DvDynamicFormElement[],
    formControls: FormOf<ReportInformationTabForm>,
    disable = true
  ): void {
    elements
      .filter(
        el =>
          el.options.key !== 'on' &&
          el.options.key !== 'onThe' &&
          el.options.key !== 'onDay' &&
          el.options.key !== 'Interval'
      )
      .forEach(element => {
        const elementKey = element.options
          .key as keyof ReportInformationTabForm;
        if (disable) {
          formControls[elementKey]?.disable({ emitEvent: false });
        } else {
          formControls[elementKey]?.enable({ emitEvent: false });
        }
      });
  }

  resetRecurrenceControls(
    formControls: FormOf<ReportInformationTabForm>
  ): void {
    toggleableRecurrenceFormControls.forEach(controlName => {
      formControls[controlName as keyof ReportInformationTabForm]?.reset();
    });
  }

  private setDailyRecurrenceText(
    controls: FormOf<ReportInformationTabForm>,
    recurrenceStartTime: string
  ): string {
    return this.translate.instant('Reports.DailyRecurrenceNew', {
      interval: controls.Interval.value || '-',
      recurrenceStartTime: recurrenceStartTime || '-',
    });
  }

  private setWeeklyRecurrenceText(
    controls: FormOf<ReportInformationTabForm>,
    recurrenceStartTime: string
  ): string {
    const selectedWeekdays = Object.keys(controls).filter(
      c =>
        Object.values(WeekdaysFormKeys).includes(c as WeekdaysFormKeys) &&
        c &&
        controls[c as keyof ReportInformationTabForm]?.value
    );

    return this.translate.instant('Reports.WeeklyRecurrenceNew', {
      interval: controls.Interval.value || '-',
      weekdays: selectedWeekdays
        .map((weekday, index) => {
          const selectedDayTranslated = this.translate.instant(
            FullWeekdaysTranslationKeys[weekday]
          );
          return (
            selectedDayTranslated +
            (index < selectedWeekdays.length - 1 ? ', ' : '')
          );
        })
        .join(''),
      recurrenceStartTime: recurrenceStartTime || '-',
    });
  }

  private setMonthlyRecurrenceText(
    controls: FormOf<ReportInformationTabForm>,
    recurrenceStartTime: string
  ): string {
    if (controls.onDay?.value) {
      return this.translate.instant('Reports.MonthlyRecurrenceWithoutOffset', {
        interval: controls.Interval.value || '-',
        dayOfTheMonth: controls.ByMonthDay?.value || '-',
        recurrenceStartTime: recurrenceStartTime || '-',
      });
    } else if (controls.onThe) {
      return this.translate.instant('Reports.MonthlyRecurrenceWithOffset', {
        interval: controls.Interval.value || '-',
        offset:
          this.reportInfoTabService.offsetDropdownItems
            .find(item => item.id === controls.Offset?.value)
            ?.label?.toLowerCase() || '-',
        dayOfTheWeek:
          this.reportInfoTabService.weekdayDropdownItems.find(
            item => item.id === controls.ByDay?.value
          )?.label || '-',
        recurrenceStartTime: recurrenceStartTime || '-',
      });
    }
    return '';
  }

  private setYearlyRecurrenceText(
    controls: FormOf<ReportInformationTabForm>,
    recurrenceStartTime: string
  ): string {
    if (controls.on?.value) {
      return this.translate.instant('Reports.YearlyRecurrenceWithoutOffset', {
        interval: controls.Interval.value || '-',
        dayOfTheMonth: controls.ByMonthDay?.value || '-',
        month:
          this.reportInfoTabService.monthDropdownItems.find(
            item => item.id === controls.ByMonthWithDay?.value
          )?.label || '-',
        recurrenceStartTime: recurrenceStartTime || '-',
      });
    } else if (controls.onThe?.value) {
      return this.translate.instant('Reports.YearlyRecurrenceWithOffset', {
        interval: controls.Interval.value || '-',
        offset:
          this.reportInfoTabService.offsetDropdownItems
            .find(item => item.id === controls.Offset?.value)
            ?.label?.toLowerCase() || '-',
        dayOfTheWeek:
          this.reportInfoTabService.weekdayDropdownItems.find(
            item => item.id === controls.ByDay?.value
          )?.label || '-',
        month:
          this.reportInfoTabService.monthDropdownItems.find(
            item => item.id === controls.ByMonthWithOffset?.value
          )?.label || '-',
        recurrenceStartTime: recurrenceStartTime || '-',
      });
    }

    return '';
  }
}
